import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, AlertTriangle, Filter, Save, ArrowLeft, Download, FileText, Loader2, Truck, ChevronDown, ChevronRight, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ['all', 'success', 'warning', 'error'];

// What to do about an error, in plain language. Matched on the backend's message.
const ERROR_HINTS = [
  [/could not geocode|could not find/i,
    "We couldn't find this place on the map. Check the spelling, or write the city with its state and country (for example \"Hosur, Tamil Nadu, India\")."],
  [/missing origin or destination/i, 'Fill in both the origin and the destination for this row.'],
  [/invalid weight value/i, 'The weight must be a plain number, such as 1200. Remove units or text from the weight cell.'],
  [/weight must be positive/i, 'Enter a weight greater than zero.'],
  [/unknown weight unit/i, 'Use one of: kg, tons (metric tonnes), grams or lbs.'],
  [/no emission factor/i, 'This vehicle has no emission factor for that transport mode. Choose a vehicle from the list in the mapping step, or correct the vehicle name or mode in your file.'],
  [/sea route|distance manually/i, 'A sea route could not be found between these points. Use port names, or enter the distance manually.'],
];

// Why a warning appeared, in plain language.
const WARNING_HINTS = [
  [/partial match/i, 'The vehicle name was not an exact match, so the closest known vehicle was used. Check that it is the right one.'],
  [/vehicle but the transport mode/i, "The vehicle doesn't belong to the transport mode on this row, so that mode's average vehicle was used."],
  [/AI resolved/i, 'The vehicle name was matched by AI. Please check it is correct.'],
  [/could not resolve|using the default/i, "The vehicle wasn't recognised, so the average vehicle for this mode was used. The result may be less accurate."],
  [/no transport mode column/i, 'Your file has no transport mode column, so road was assumed.'],
  [/unknown transport mode/i, "The transport mode on this row wasn't recognised, so road was assumed. Use road, rail, air or sea."],
  [/manual override/i, 'You chose this vehicle yourself in the mapping step.'],
  [/no factor for/i, 'There is no factor for this exact vehicle and fuel, so the closest available factor was used.'],
  [/unknown fuel type/i, "The fuel wasn't recognised, so diesel was assumed. Use diesel, petrol, CNG or LPG."],
  [/electric vehicle/i, 'There is no electric-vehicle freight factor in the dataset, so the diesel factor for the same vehicle size was used.'],
  [/converted weight/i, 'The weight was converted to kilograms before calculating.'],
  [/usually carries/i, 'This load is heavier than one vehicle of this size can carry, so it is counted as several trips. The result stays correct for that, but check the vehicle type in your file.'],
];

const PAGE_SIZE = 100;

// How reliable the file's numbers are, at a glance: how each vehicle was decided and what to check.
function QualitySummary({ quality, totalRows }) {
  if (!quality) return null;
  const m = quality.vehicle_match || {};
  const f = quality.flags || {};
  const exact = (m.catalogue || 0) + (m.saved || 0) + (m.manual || 0);
  const items = [
    { n: exact, label: 'vehicles matched exactly', tone: 'text-green-700' },
    { n: m.ai || 0, label: 'matched by AI — worth a quick check', tone: 'text-sky-700' },
    { n: (m.default || 0) + (m.not_given || 0), label: 'used the average vehicle (not recognised or not given)', tone: 'text-amber-700' },
    { n: f.size_unknown || 0, label: 'trucks of unknown size (all-truck average)', tone: 'text-amber-700' },
    { n: f.over_capacity || 0, label: 'loads heavier than one vehicle — counted as several trips', tone: 'text-amber-700' },
  ].filter((i) => i.n > 0);
  if (items.length === 0) return null;
  return (
    <Card className="pro-card">
      <CardContent className="p-4">
        <p className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-sky-600" /> Data quality for {fmt(totalRows, 0)} rows
        </p>
        <ul className="grid gap-1 sm:grid-cols-2 text-sm">
          {items.map((i) => (
            <li key={i.label} className={i.tone}>
              <span className="font-semibold tabular-nums">{fmt(i.n, 0)}</span> {i.label}
            </li>
          ))}
        </ul>
        {((m.default || 0) + (f.over_capacity || 0)) > 0 && (
          <p className="text-xs text-gray-500 mt-2">
            Tip: use the Warning filter to review these rows. Correct a vehicle once in the mapping
            step and tick “Remember” — your next uploads will use it automatically.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const hintFor = (text, hints) => (hints.find(([re]) => re.test(text || '')) || [])[1];

const fmt = (n, digits = 2) =>
  n === null || n === undefined || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: digits });

function NotesSummary({ row, open, onToggle }) {
  const warnings = row.warnings || [];
  if (!row.error && warnings.length === 0) {
    return row.factor_value != null ? (
      <button type="button" onClick={onToggle} aria-expanded={open}
        className="text-xs text-sky-600 hover:underline flex items-center gap-1">
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {open ? 'Hide calculation' : 'View calculation'}
      </button>
    ) : <span className="text-gray-300">—</span>;
  }
  return (
    <button type="button" onClick={onToggle}
      className="flex items-start gap-1.5 text-left text-xs w-full group" aria-expanded={open}>
      {open ? <ChevronDown className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
      <span className="min-w-0">
        {row.error && <span className="block text-red-600 font-medium line-clamp-2">{row.error}</span>}
        {warnings.length > 0 && (
          <span className="block text-amber-700">
            {warnings.length} warning{warnings.length > 1 ? 's' : ''}
          </span>
        )}
        <span className="block text-sky-600 group-hover:underline">{open ? 'Hide details' : 'View details'}</span>
      </span>
    </button>
  );
}

function RowDetails({ row }) {
  const warnings = row.warnings || [];
  const tonnes = row.weight_kg != null ? row.weight_kg / 1000 : null;
  const perTkm = (row.factor_unit || '').toLowerCase().includes('tonne');
  const input = Object.entries(row.input_data || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined);
  return (
    <div className="space-y-4 text-sm">
      {row.error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="font-semibold text-red-700 flex items-center gap-1.5"><XCircle className="w-4 h-4" /> Error — this row was not calculated</p>
          <p className="mt-1 text-red-800 whitespace-pre-wrap break-words">{row.error}</p>
          {hintFor(row.error, ERROR_HINTS) && (
            <p className="mt-2 text-red-700"><strong>How to fix:</strong> {hintFor(row.error, ERROR_HINTS)}</p>
          )}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="font-semibold text-amber-800 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> {warnings.length} warning{warnings.length > 1 ? 's' : ''} — calculated, but please check
          </p>
          <ol className="mt-2 space-y-2 list-decimal list-inside">
            {warnings.map((w, i) => (
              <li key={i} className="text-amber-900">
                <span className="whitespace-pre-wrap break-words">{w}</span>
                {hintFor(w, WARNING_HINTS) && (
                  <span className="block ml-5 text-amber-700 text-xs">{hintFor(w, WARNING_HINTS)}</span>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
      {!row.error && row.factor_value != null && (
        <div className="rounded-md border border-sky-200 bg-sky-50 p-3">
          <p className="font-semibold text-sky-800 flex items-center gap-1.5"><Info className="w-4 h-4" /> How this row was calculated</p>
          <dl className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-gray-700">
            <dt className="text-gray-500">Vehicle in your file</dt><dd>{row.vehicle_input || '— (not given)'}</dd>
            <dt className="text-gray-500">Vehicle used</dt><dd>{row.vehicle_label} <span className="text-gray-400 font-mono text-xs">({row.vehicle_type})</span></dd>
            <dt className="text-gray-500">Transport mode</dt><dd>{row.transport_mode}</dd>
            <dt className="text-gray-500">Weight</dt><dd>{fmt(row.weight_kg)} kg ({fmt(tonnes, 4)} t)</dd>
            <dt className="text-gray-500">Distance</dt><dd>{fmt(row.distance_km, 1)} km</dd>
            <dt className="text-gray-500">Emission factor</dt><dd>{row.factor_value} {row.factor_unit}{row.factor_source ? ` — ${row.factor_source}` : ''}</dd>
          </dl>
          <p className="mt-2 font-mono text-xs text-sky-900 break-words">
            {perTkm
              ? `${row.factor_value} × ${fmt(tonnes, 4)} t × ${fmt(row.distance_km, 3)} km = ${fmt(row.emissions, 4)} kg CO₂e`
              : `${row.factor_value} × ${fmt(row.distance_km, 3)} km = ${fmt(row.emissions, 4)} kg CO₂e (per vehicle-km, does not depend on weight)`}
          </p>
        </div>
      )}
      {input.length > 0 && (
        <div>
          <p className="font-semibold text-gray-600 mb-1">Values in your file (row {row.row_index + 1})</p>
          <div className="flex flex-wrap gap-2">
            {input.map(([k, v]) => (
              <span key={k} className="rounded border border-gray-200 bg-white px-2 py-0.5 text-xs">
                <span className="text-gray-500">{k}:</span> <span className="text-gray-800 break-all">{String(v)}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ResultsTable({ results, summary, onSave, onBack, sessionId, isSaving, onOpenInSingleShipment }) {
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [page, setPage] = useState(0);

  const toggleExpanded = (idx) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // Pre-select successful rows when results load (handles race condition)
  useEffect(() => {
    if (results.length > 0) {
      setSelected(new Set(
        results
          .filter((r) => r.status === 'success' || r.status === 'warning')
          .map((r) => r.row_index)
      ));
    }
  }, [results]);

  const filtered = useMemo(() => {
    if (filter === 'all') return results;
    return results.filter((r) => r.status === filter);
  }, [results, filter]);

  // One page at a time: rendering every row of a 5,000-row file froze the browser.
  useEffect(() => { setPage(0); }, [filter, results]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filtered, page],
  );
  const saveableCount = useMemo(() => results.filter((r) => r.status !== 'error').length, [results]);

  // "Expand all" works on the rows on screen.
  const rowsWithNotes = pageRows.filter((r) => r.error || (r.warnings || []).length > 0);
  const allExpanded = rowsWithNotes.length > 0 && rowsWithNotes.every((r) => expanded.has(r.row_index));
  const toggleExpandAll = () =>
    setExpanded(allExpanded ? new Set() : new Set(rowsWithNotes.map((r) => r.row_index)));

  const toggleRow = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API}/agent/export/${sessionId}/${format}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `export.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const toggleAll = () => {
    const saveable = results.filter((r) => r.status !== 'error').map((r) => r.row_index);
    if (selected.size === saveable.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(saveable));
    }
  };

  const statusIcon = (status) => {
    if (status === 'success') return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    if (status === 'warning') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-800 tabular-nums">{fmt(summary.total_rows, 0)}</p>
            <p className="text-xs text-gray-500">Total Rows</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600 tabular-nums">{fmt(summary.successful, 0)}</p>
            <p className="text-xs text-gray-500">Successful</p>
          </Card>
          <Card className="p-4 text-center">
            {/* Large files total millions of kg: show tonnes from 10 t up so the number stays readable. */}
            {summary.total_emissions >= 10000 ? (
              <>
                <p className="text-2xl font-bold text-sky-600 tabular-nums">{fmt(summary.total_emissions / 1000, 1)}</p>
                <p className="text-xs text-gray-500">Total tCO2e</p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-sky-600 tabular-nums">{fmt(summary.total_emissions, 2)}</p>
                <p className="text-xs text-gray-500">Total kgCO2e</p>
              </>
            )}
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-600 tabular-nums">{fmt(summary.total_distance, 0)}</p>
            <p className="text-xs text-gray-500">Total km</p>
          </Card>
        </div>
      )}

      <QualitySummary quality={summary?.quality} totalRows={summary?.total_rows || results.length} />

      {/* Results Table */}
      <Card className="pro-card">
        <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-gray-800">
              Calculation Results
            </CardTitle>
            <div className="flex items-center gap-2">
              {rowsWithNotes.length > 0 && (
                <button
                  type="button"
                  onClick={toggleExpandAll}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-800 hover:bg-amber-100 mr-2"
                >
                  {allExpanded ? 'Collapse all details' : `Expand all details (${rowsWithNotes.length})`}
                </button>
              )}
              <Filter className="w-4 h-4 text-gray-400" />
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    filter === f
                      ? 'bg-sky-100 text-sky-700'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selected.size === saveableCount && selected.size > 0}
                      onChange={toggleAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-gray-600 font-medium">Row</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-medium">Status</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-medium">Origin</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-medium">Destination</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-medium">Mode</th>
                  <th className="px-4 py-3 text-right text-gray-600 font-medium">Distance (km)</th>
                  <th className="px-4 py-3 text-right text-gray-600 font-medium">Emissions (kgCO2e)</th>
                  <th className="px-4 py-3 text-left text-gray-600 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <React.Fragment key={row.row_index}>
                  <tr
                    className={`border-b border-gray-100 transition-colors ${
                      selected.has(row.row_index) ? 'bg-sky-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-3">
                      {row.status !== 'error' && (
                        <input
                          type="checkbox"
                          checked={selected.has(row.row_index)}
                          onChange={() => toggleRow(row.row_index)}
                          className="rounded"
                        />
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.row_index + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {statusIcon(row.status)}
                        <Badge
                          variant={
                            row.status === 'success'
                              ? 'default'
                              : row.status === 'warning'
                              ? 'secondary'
                              : 'destructive'
                          }
                          className="text-[10px]"
                        >
                          {row.status}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-[150px] truncate">
                      {row.origin || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-[150px] truncate">
                      {row.destination || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{row.transport_mode || '—'}</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-700">
                      {row.distance_km?.toFixed(1) || '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-gray-800">
                      {row.emissions?.toFixed(4) || '—'}
                    </td>
                    <td className="px-4 py-3 align-top min-w-[180px] max-w-[260px]">
                      {/* The full error AND warnings live in the expandable panel below. This cell
                          used to show warnings OR the error, cut to one 200px line. */}
                      <NotesSummary
                        row={row}
                        open={expanded.has(row.row_index)}
                        onToggle={() => toggleExpanded(row.row_index)}
                      />
                    </td>
                  </tr>
                  {expanded.has(row.row_index) && (
                    <tr className="border-b border-gray-100 bg-gray-50/60">
                      <td colSpan={9} className="px-6 py-4">
                        <RowDetails row={row} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="p-8 text-center text-gray-400">
              No rows match the selected filter.
            </div>
          )}
          {filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
              <span className="tabular-nums">
                Rows {fmt(page * PAGE_SIZE + 1, 0)}–{fmt(Math.min((page + 1) * PAGE_SIZE, filtered.length), 0)} of {fmt(filtered.length, 0)}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <span className="tabular-nums">Page {page + 1} of {pageCount}</span>
                <Button variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          {sessionId && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('csv')}
              >
                <Download className="w-4 h-4 mr-1" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExport('pdf')}
              >
                <FileText className="w-4 h-4 mr-1" />
                PDF
              </Button>
            </>
          )}
          {onOpenInSingleShipment && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const rows = results.filter((r) => selected.has(r.row_index));
                if (rows.length === 0) {
                  toast.error('Select at least one row to open in Single Shipment.');
                  return;
                }
                onOpenInSingleShipment(rows);
              }}
              title="Load selected rows as legs in the Single Shipment form"
            >
              <Truck className="w-4 h-4 mr-1" />
              Open in Single Shipment
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {selected.size} row{selected.size !== 1 ? 's' : ''} selected
          </span>
          <Button
            onClick={() => onSave(Array.from(selected))}
            disabled={selected.size === 0 || isSaving}
            className="bg-green-600 hover:bg-green-700"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save to Dashboard
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
